import type * as smartWorkspaces from "@eu.mdotm.api/smart-workspaces";
import {
	type Description,
	type SelectableBasket,
	type Tag,
	type UserInstrumentDto,
	InstrumentsCustomizationControllerV3ApiFactory,
	type UserInstrumentClassificationDto,
} from "@eu.mdotm.api/sphere";
import {
	type OrderBy,
	CircularProgressBar,
	Icon,
	LocalOverlay,
	Pagination,
	type PaginationResponse,
	Row,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { useDeepEqualEffect, useRefProxyWithState } from "@mdotm/shadcn/legacy/react-extensions";
import { readAtom, useAtomValue, WithAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { BulkActions, Button } from "@mdotm/shadcn/mdotm/components";
import { type MaybePromise, useAsync } from "@mdotm/utils/promise";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import type {
	CellValueChangedEvent,
	ColDef,
	ColumnState,
	GetContextMenuItems,
	SortChangedEvent,
} from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import axios from "axios";
import equal from "fast-deep-equal";
import { Map } from "immutable";
import { type Ref, useCallback, useEffect, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";
import { reportPlatformError, runWithErrorReporting } from "$root/api/error-reporting/report";
import { useApiGen } from "$root/api/hooks";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { useEventBus, useGroupEventBus } from "$root/event-bus";
import { UserRole, useUserValue } from "$root/functional-areas/user";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { usePaginationTools } from "$root/hooks/usePaginationTools";
import { useLocaleFormatters } from "$root/localization/hooks";
import { platformToast } from "$root/notification-system/toast";
import {
	AgGridWithModules,
	useAgGridRowListener,
	useAgGridSelectedNodes,
} from "$root/third-party-integrations/ag-grid/ag-grid";
import type { CustomAxiosError } from "$root/third-party-integrations/axios";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useQueryNoRefetch, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { queryClientKeys, typedQueryClient } from "$root/third-party-integrations/react-query/typedQueryClient";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { columnChooserActionLoadingContextKey } from "$root/ui-lib/table/custom-column/setting/base-cell";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { type TaskQueue, coalesce, downloadContentDisposition, parallelize, seconds } from "$root/utils";
import { Card } from "$root/widgets-architecture/layout/Card";
import { SavedAtTaskQueueIndicator } from "../../../components/SavedAtTaskQueueIndicator";
import {
	applyPropagationState,
	isPropagateNowVisible,
	type PropagationState,
	propagateNowState,
	propagationMessage,
	propagationRepository,
	usePropagationEvents,
} from "../propagation";
import { instrumentsRepository } from "../repository";
import { userClassifications$ } from "../user-classifications";
import { enabledClassificationUuids, getPaginatedUserInstruments } from "./helper";
import { useInstrumentCustomisationState } from "./InstrumentCustomisationStore";
import InstrumentCustomisationTableSidePanel, {
	type InstrumentCustomisationSettings,
	type InstrumentCustomisationTableSidePanelProps,
	type TagFilter,
} from "./SidePanelColumnFilters";
import { instrumentUpdateQueue, instrumentUpdateQueueState$ } from "./updateQueue";

export const instrumentCommentaryGenerationCode = {
	DATA_NOT_AVAILABLE: "DATA_NOT_AVAILABLE",
	COMMENTARY_GENERATION_ERROR: "COMMENTARY_GENERATION_ERROR",
};

import type { PaginatedUserInstrumentsRequestProps } from "./types";

const INSTRUMENT_COL_CONCEPT_IDS: Record<string, string> = {
	NAME: "systemId: instrument:name",
	DESCRIPTION: "systemId: instrument:description",
	ISIN: "systemId: instrument:isin",
	CUSIP: "systemId: instrument:cusip",
	BLOOMBERG_TICKER: "systemId: instrument:bloombergTicker",
	ALIAS: "systemId: instrument:alias",
	RISK_MODEL: "systemId: instrument:riskModel",
};

/** @internal — exported for tests */
export function classificationConceptId(col: UserInstrumentClassificationDto): string {
	if (col.fieldType === "TAG") {
		return `instrumentTagId: ${col.classificationUuid}`;
	}
	if (col.fieldType === "QUALITY") {
		return `instrumentQualityId: ${col.classificationUuid}`;
	}
	return `instrumentScoreId: ${col.classificationUuid}`;
}

/** @internal — exported for tests */
export function buildInstrumentExportConfig({
	columnState,
	classificationMetadata,
	skip,
	take,
	searchQuery,
	baskets,
	filters,
}: {
	columnState: ColumnState[];
	classificationMetadata: UserInstrumentClassificationDto[];
	skip: number;
	take: number;
	searchQuery: string | undefined;
	baskets?: SelectableBasket[];
	filters?: TagFilter[];
}): smartWorkspaces.DataRequestDTO {
	const concepts: smartWorkspaces.ParameterizedConceptDTO[] = [];

	for (const state of columnState) {
		if (state.hide || !state.colId) {
			continue;
		}
		const fixedConceptId = INSTRUMENT_COL_CONCEPT_IDS[state.colId];
		if (fixedConceptId) {
			concepts.push({
				uuid: crypto.randomUUID(),
				conceptId: fixedConceptId,
				label: state.colId,
				source: "CURRENT",
			});
		} else {
			const classification = classificationMetadata.find((c) => c.classificationUuid === state.colId);
			if (classification) {
				concepts.push({
					uuid: crypto.randomUUID(),
					conceptId: classificationConceptId(classification),
					label: classification.name ?? state.colId,
					source: "CURRENT",
				});
			}
		}
	}

	const nameConcept = concepts.find((c) => c.conceptId === INSTRUMENT_COL_CONCEPT_IDS.NAME);
	const predicate: smartWorkspaces.OperatorDTO | undefined =
		searchQuery && nameConcept?.uuid
			? {
					type: "COMPARISON",
					keyword: "contains",
					parameterizedConceptUuid: nameConcept.uuid,
					arguments: [searchQuery as unknown as object],
				}
			: undefined;

	const sortedColumnState = columnState
		.filter((s) => s.sort != null)
		.sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
		.at(0);

	const sortConceptUuid = sortedColumnState
		? concepts.find((c) => {
				const fixedConceptId = INSTRUMENT_COL_CONCEPT_IDS[sortedColumnState.colId ?? ""];
				return fixedConceptId ? c.conceptId === fixedConceptId : c.label === sortedColumnState.colId;
			})?.uuid
		: concepts.find((c) => c.conceptId === INSTRUMENT_COL_CONCEPT_IDS.NAME)?.uuid;

	const sorting: smartWorkspaces.DataRequestDTO["sorting"] = sortConceptUuid
		? [
				{
					parameterizedConceptUuid: sortConceptUuid,
					direction: sortedColumnState?.sort === "desc" ? "DESC" : "ASC",
				},
			]
		: undefined;

	return {
		concepts,
		dataProvider: {
			dataProviderId: "userInstrument",
			parameters: {
				baskets: baskets ?? [],
				tagValue:
					filters?.map((f) => ({
						classificationUuid: f.classificationUuid,
						values: f.values,
					})) ?? [],
			},
		},
		projection: concepts.map((c) => c.uuid!),
		predicate,
		sorting,
		limit: take,
		offset: skip,
	};
}

function InstrumentCustomizationTable({
	gridRef,
}: {
	gridRef?: Ref<AgGridReact<UserInstrumentDto>>;
}): React.JSX.Element {
	const instrumentCustomizationV3Api = useApiGen(InstrumentsCustomizationControllerV3ApiFactory);

	// The propagation payload is only visible where the API is actually called, so this returns it to
	// the inner component that owns the indicator row and the `Apply now` button.
	const onChangeCell = async (
		tickerName: string,
		patchableField: PatchableFields,
	): Promise<PropagationState | undefined> => {
		const { riskModel, description, alias, ...rest } = patchableField;

		if (riskModel) {
			if (riskModel.length === 0) {
				throw new Error("unexpected empty risk model dispatched on cell change");
			}
			const { data } = await instrumentCustomizationV3Api.updateRiskModel(tickerName, riskModel);
			return data.propagation;
		}

		if (alias) {
			const { data } = await instrumentCustomizationV3Api.updateAlias(tickerName, alias);
			return data.propagation;
		}

		const { data } = await instrumentCustomizationV3Api.updateInstrumentFields(tickerName, {
			...rest,
			description: description?.value,
		});
		return data.propagation;
	};

	const userClassifications = useAtomValue(userClassifications$);

	return (
		<InstrumentCustomizationTableInner
			gridRef={gridRef}
			classificationMetadata={userClassifications}
			rowsProvider={getPaginatedUserInstruments}
			onChangeCell={onChangeCell}
			selectableBasketProvider={async () => {
				const selectableBaskets = await instrumentsRepository.selectableBaskets.fetchQuery();
				const basketsWithoutSphereTemplates = selectableBaskets.filter(
					(x) => x.basketIdentifier && x.basketName && x.basketType && x.basketType !== "TEMPLATE",
				);
				return basketsWithoutSphereTemplates;
			}}
			selectableUserClassificationsProvider={() => Promise.resolve(userClassifications)}
		/>
	);
}

type PatchableFields = Pick<
	UserInstrumentDto,
	"riskModel" | "tags" | "scores" | "name" | "description" | "qualities"
> & {
	alias?: string;
};

export type InstrumentCustomizationTableInnerProps = {
	rowsProvider: (request: PaginatedUserInstrumentsRequestProps) => MaybePromise<PaginationResponse<UserInstrumentDto>>;
	classificationMetadata: UserInstrumentClassificationDto[];
	selectableBasketProvider: InstrumentCustomisationTableSidePanelProps["selectableBasketProvider"];
	selectableUserClassificationsProvider: InstrumentCustomisationTableSidePanelProps["selectableUserClassificationsProvider"];
	/**
	 * Resolves with the propagation state carried by the `PATCH` response, or `undefined`
	 * when the caller has none to report — e.g. a Storybook story rendering with no API layer.
	 */
	onChangeCell: (tickerName: string, patchableField: PatchableFields) => Promise<PropagationState | undefined>;
	gridRef?: Ref<AgGridReact<UserInstrumentDto>>;
};

export function InstrumentCustomizationTableInner({
	rowsProvider,
	selectableBasketProvider,
	selectableUserClassificationsProvider,
	classificationMetadata,
	onChangeCell,
	gridRef,
	...forward
}: InstrumentCustomizationTableInnerProps): React.JSX.Element {
	const [gridEl, setGridEl] = useRefProxyWithState(null, gridRef);

	const { instrumentCustomisationStore, setInstrumentCustomisationStore } = useInstrumentCustomisationState();
	const [instrumentCustomisationSettings, setInstrumentCustomisationSettings] =
		useState<InstrumentCustomisationSettings>({
			search: instrumentCustomisationStore.search,
			selectedBaskets: instrumentCustomisationStore.selectedBaskets,
			selectedfilters: Map(instrumentCustomisationStore.selectedfilters),
		});

	const { orderBy, skip, take, query } = usePaginationTools({
		initialQuery: instrumentCustomisationStore.search,
	});

	const validTagFilters = useMemo<Tag[] | undefined>(() => {
		const filters = instrumentCustomisationSettings.selectedfilters?.valueSeq().toArray();

		return filters?.filter((filter) => filter.classificationUuid && filter.values && filter.values.length > 0);
	}, [instrumentCustomisationSettings.selectedfilters]);

	const [columnState, setColumnState] = useState<ColumnState[]>([]);
	useAgGridRowListener(gridEl, "stateUpdated", (event) => {
		const currentColumnState = event.api.getColumnState();
		setColumnState(() => currentColumnState);
	});

	const [userClassificationIds, setUserClassificationIds] = useState(
		enabledClassificationUuids(classificationMetadata, columnState),
	);

	useDeepEqualEffect(() => {
		setUserClassificationIds((latestUserClassificationIds) => {
			const newUserClassificationIds = enabledClassificationUuids(classificationMetadata, columnState);
			const foundAllClassification = newUserClassificationIds.every((id) => latestUserClassificationIds.includes(id));
			if (foundAllClassification && newUserClassificationIds.length === latestUserClassificationIds.length) {
				return latestUserClassificationIds;
			}

			return newUserClassificationIds;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [columnState, classificationMetadata]);

	const rowsQuery = useQueryNoRefetch({
		queryKey: [
			queryClientKeys.userInstrumentsBaseQueryKey /*filters*/,
			skip.value,
			take.value,
			orderBy.value,
			instrumentCustomisationSettings.selectedBaskets,
			validTagFilters,
			query.value,
			userClassificationIds,
		],
		queryFn: () =>
			rowsProvider({
				skip: skip.value,
				take: take.value,
				filters: {
					query: query.value,
					fields: userClassificationIds,
					selectedBaskets: instrumentCustomisationSettings.selectedBaskets,
					tags: validTagFilters,
				},
				orderBy: orderBy.value,
			}),
		keepPreviousData: true,
	});

	const rows = useMemo(
		() =>
			rowsQuery.data?.items.flatMap((x): UserInstrumentDto[] =>
				x.tickerName ? [{ ...x, tickerName: x.tickerName }] : [],
			) ?? [],
		[rowsQuery.data],
	);

	/**
	 * Whether the save indicator has anything to say *about this visit*. The queue is a module
	 * singleton, so its state can carry a `Saved at` over from an earlier visit — misleading on a page
	 * whose customizations are customer-wide, since a colleague may have edited in between.
	 *
	 * Latched, never lowered: it starts true when we arrive with a write still in flight (that one *is*
	 * live, and it also produces a legitimately current `Saved at` when it lands) and is raised by
	 * `enqueueSave`. Lowering it again on drain would make the row disappear the moment it had
	 * something to report. `readAtom` rather than `useAtomValue` on purpose — subscribing here would
	 * re-render the grid on every cell commit, which is what the atom exists to avoid.
	 */
	const [hasSavesThisVisit, setHasSavesThisVisit] = useState(() => readAtom(instrumentUpdateQueueState$).isRunning);
	const enqueueSave = useCallback<TaskQueue["enqueue"]>((task) => {
		setHasSavesThisVisit(true);
		return instrumentUpdateQueue.enqueue(task);
	}, []);

	const { t } = useTranslation();
	const { formatTime, formatDateTime } = useLocaleFormatters();
	const user = useUserValue();

	/**
	 * Cosmetic mirror of the `@PreAuthorize` on the flush endpoint — the server-side check is
	 * the real one. A flush rebuilds every dependent Portfolio Studio entity for the whole customer, so
	 * it is not something a `GUEST` gets to trigger.
	 */
	const canPropagate = useMemo(
		() => user.roles.some((role) => role === UserRole.MANAGER || role === UserRole.ADMIN || role === UserRole.ROOT),
		[user.roles],
	);

	/**
	 * Propagation of the customizations to the Portfolio Studio entities that depend on them.
	 * Fed from five sources, every one of them guarded by the revision so that an out-of-order arrival
	 * can never resurrect an older state:
	 *
	 * 1. the state `GET` on mount (plus the reconciling re-reads below),
	 * 2. every `PATCH` response, via `onChangeCell`,
	 * 3. the flush `POST` response,
	 * 4. the schedule-changed event,
	 * 5. the started and done events — the same events the global handler toasts on, because without
	 *    them this row would keep promising a future run for the whole duration of a propagation and
	 *    the button would never learn that one is running.
	 */
	const [propagation, setPropagation] = useState<PropagationState | null>(null);

	/**
	 * Commentary generations that have been dispatched but whose write has not landed yet. They do not
	 * go through the save queue and complete asynchronously long after their HTTP call resolves, so
	 * the flush button has to track them itself. Keyed by the identifier the
	 * `instrument-commentary-update` event reports, which is the grid's own row id.
	 */
	const [pendingCommentaryGenerations, setPendingCommentaryGenerations] = useState<ReadonlySet<string>>(
		() => new Set(),
	);

	const trackCommentaryGeneration = useCallback((tickerName: string) => {
		setPendingCommentaryGenerations((current) => new Set(current).add(tickerName));
	}, []);

	// Sources 1 and the tab-focus reconcile. `useQuery` reads on mount and — via the query's
	// `refetchOnWindowFocus` — re-reads whenever the tab regains focus, the two triggers that used to
	// be hand-rolled effects. The result is not consumed reactively: it is folded into the local
	// state through the same revision guard as every other source, so an out-of-order read can never
	// walk the state back.
	const propagationStateQuery = propagationRepository.getPropagationState.useQuery();
	const reconcilePropagationState = useCallback(() => {
		propagationStateQuery.refetch().catch(noop);
	}, [propagationStateQuery]);

	useQueryReactor(propagationStateQuery, {
		onSuccess: (state) => setPropagation((current) => applyPropagationState(current, state)),
		// Reported but never toasted: this read also runs on every stream reopen and on every tab
		// focus, so a failing one must not turn into recurring noise.
		onError: (err) =>
			reportPlatformError(err, "ERROR", "instrument-customisation", {
				message: "unable to read the instrument propagation state",
			}),
	});

	// Reconcile after any SSE gap. The stream is torn down and rebuilt routinely — on every
	// access-token renewal and after an error retry — and the restart discards the browser's own
	// `Last-Event-ID` replay, so a `done` lost in that window would otherwise leave this row saying
	// "Applying to portfolios…" with the button disabled until the page is remounted.
	useEventBus("server-events-stream-open", reconcilePropagationState);

	// Sources 4 and 5. All three events carry the full state, so one handler and one guard cover them.
	usePropagationEvents((event) => setPropagation((current) => applyPropagationState(current, event.propagation)));

	const onPropagateNow = useCallback(async () => {
		// Tracked on the click, not on success: a 409 is still a user asking to skip the wait. Read
		// *before* the flush — the response sets `scheduledAt` to null.
		trackMixPanelEvent("instrument.entityAction.propagateNow", {
			scheduledAt: propagation?.scheduledAt ?? null,
		});
		try {
			// Source 3.
			const state = await propagationRepository.propagateNow();
			setPropagation((current) => applyPropagationState(current, state));
		} catch (err) {
			// Every backend `RestException` ships as HTTP 510 with the real status in the body's `code`
			// (`409` = a normal race, `429` = flushed inside the minimum interval) and a `message` — so we
			// branch on `code`, not the HTTP status, mirroring the rest of the app.
			const restError = axios.isAxiosError<CustomAxiosError>(err) ? err.response?.data : undefined;
			if (restError?.code === 409) {
				// A normal race, not an error: someone else flushed, or the debounce fired between render
				// and click. Re-read the state — it reports the propagation as running — and stay quiet.
				reconcilePropagationState();
				return;
			}
			if (restError?.code === 429) {
				// Flushed again inside the backend's minimum interval. Surface the server's own message
				// ("Propagation was flushed less than 60s ago.") — it names the actual wait.
				platformToast({
					children: restError.message ?? "Too many propagation requests, please try again in a moment",
					severity: "warning",
					icon: "Icon-full-alert",
				});
				return;
			}
			platformToast({
				children: t("SOMETHING_WENT_WRONG"),
				severity: "error",
				icon: "Icon-full-alert",
			});
			reportPlatformError(err, "ERROR", "instrument-customisation", {
				message: "unable to apply the instrument customizations to portfolios",
			});
		}
	}, [propagation?.scheduledAt, reconcilePropagationState, t]);

	const propagationRow = propagationMessage(propagation, { formatTime, formatDateTime }, new Date());
	const showPropagateNow = isPropagateNowVisible({ propagation, canPropagate });

	const exportRefs = useUnsafeUpdatedRef({
		instrumentCustomisationSettings,
		classificationMetadata,
		gridEl,
	});

	const [runExport, exportInstrumentState] = useAsync(async () => {
		const { classificationMetadata, instrumentCustomisationSettings, gridEl } = exportRefs.current;
		const columnState = gridEl?.api.getColumnState() ?? [];
		const config = buildInstrumentExportConfig({
			columnState,
			classificationMetadata,
			skip: 0,
			take: 1_000_000,
			searchQuery: instrumentCustomisationSettings.search,
			baskets: instrumentCustomisationSettings.selectedBaskets,
			filters: instrumentCustomisationSettings.selectedfilters?.valueSeq().toArray(),
		});
		await runWithErrorReporting(
			async () => {
				const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
				await downloadContentDisposition(file);
			},
			{
				area: "instrument-customisation",
				attemptedOperation: {
					message: "unable to export using, op: exportDataAsExcel",
					payload: JSON.stringify(config),
				},
			},
		).catch(noop);
	});

	// The column-chooser menu entry takes a `() => void`, so it drops the promise `runExport` returns.
	// `unpromisify` terminates it; memoised because the columns definition is keyed on this identity.
	const onExport = useMemo(() => unpromisify(runExport), [runExport]);

	const isExporting = exportInstrumentState.kind === "loading";

	useEffect(() => {
		if (!gridEl?.api) {
			return;
		}
		gridEl.api.setGridOption("context", {
			[columnChooserActionLoadingContextKey]: isExporting,
		});
		gridEl.api.refreshHeader();
	}, [isExporting, gridEl]);

	const initialColumns = columnsDefinition.useInstrumentCustomisation({
		onExport,
	});

	useGroupEventBus(
		"instrument-commentary-update",
		(events) => {
			// A generation is outstanding until its write lands, which is what this event reports — for
			// failures too, so a failed generation cannot leave the flush button disabled forever.
			setPendingCommentaryGenerations((current) => {
				const next = new Set(current);
				events.forEach((event) => {
					if (event.isin != null) {
						next.delete(event.isin);
					}
				});
				return next.size === current.size ? current : next;
			});

			events.forEach((event) => {
				if (event.isin == null) {
					return;
				}

				const id = event.isin;
				const rowNode = gridEl?.api.getRowNode(id);
				if (rowNode) {
					rowNode.updateData({
						...rowNode.data,
						description: {
							...coalesce(rowNode.data?.description, {}),
							status: event.descriptionStatus,
							date: new Date().toISOString(),
							value: event.description,
						},
					});
				}
			});
		},
		{ timeout: seconds(1) },
	);

	// An instruments import finishing is only known to the backend, and it has its own topic (the
	// backend spells it "instrumens"). Invalidating the base key rather than refetching this page's
	// query also drops the other cached pages, so paging back after an import doesn't show rows from
	// before it.
	useGroupEventBus(
		"instrumens-import-update",
		() => {
			typedQueryClient().invalidateQueries(queryClientKeys.userInstrumentsBaseQueryKey).catch(noop);
		},
		{ timeout: seconds(2) },
	);

	const selectedNodes = useAgGridSelectedNodes(gridEl);

	return (
		<div className="flex-1 flex flex-col min-h-0 min-w-0">
			<div className="min-w-0 min-h-0 flex-1 flex relative z-0">
				<InstrumentCustomisationTableSidePanel
					expand={instrumentCustomisationStore.expanded}
					onExpandChange={() =>
						setInstrumentCustomisationStore({
							...instrumentCustomisationStore,
							expanded: !instrumentCustomisationStore.expanded,
						})
					}
					selectableBasketProvider={selectableBasketProvider}
					selectableUserClassificationsProvider={selectableUserClassificationsProvider}
					initialInstrumentCustomisationSettings={instrumentCustomisationSettings}
					onChangeInstrumentCustomisationSettings={(newSettings) => {
						query.set(newSettings.search);
						setInstrumentCustomisationSettings(newSettings);
						setInstrumentCustomisationStore({
							expanded: instrumentCustomisationStore.expanded,
							search: newSettings.search,
							selectedBaskets: newSettings.selectedBaskets ?? [],
							selectedfilters: newSettings.selectedfilters?.toArray() ?? [],
						});
					}}
				/>
				<Card className="px-4 py-6 min-w-0 flex-1 relative">
					{/*
					 * The save indicator used to be an absolutely-positioned overlay over the grid. It now
					 * shares an in-flow row with the propagation state and the flush action: the
					 * action is a *sibling* of the indicator, never a child, since inside the indicator's
					 * `Sandwich` it would be duplicated into the invisible sizing placeholder and an E2E
					 * click could land on the hidden copy.
					 */}
					<Row alignItems="center" justifyContent="space-between" gap={16} flexGrow={0} className="mb-2">
						<Row minSize="0" alignItems="center" gap={16}>
							{hasSavesThisVisit && (
								<SavedAtTaskQueueIndicator className="shrink-0" taskQueueState$={instrumentUpdateQueueState$} />
							)}
							{(propagationRow || showPropagateNow) && (
								<Row minSize="0" alignItems="center" gap={8}>
									{propagationRow && (
										<Row minSize="0" flexGrow={0} alignItems="center" gap={4} data-kind={propagationRow.kind}>
											<Icon
												icon={propagationRow.icon}
												size={16}
												color={propagationRow.kind === "error" ? themeCSSVars.global_palette_warning_600 : undefined}
											/>
											<Text
												as="div"
												type="Body/S/Medium"
												className="truncate"
												data-qualifier="InstrumentCustomization/Sync/Propagation"
											>
												{propagationRow.label}
											</Text>
										</Row>
									)}
									{/*
									 * Visibility is driven by the server's propagation state only; the save queue's
									 * `isRunning` — which flips true on every cell commit — merely disables, so the
									 * action never appears and disappears while the user works. Read through
									 * `WithAtom` to keep those commits from re-rendering the grid.
									 */}
									{showPropagateNow && (
										<WithAtom atom$={instrumentUpdateQueueState$}>
											{({ isRunning }) => (
												<Button
													palette="secondary"
													size="x-small"
													className="shrink-0 mb-0.5"
													disabled={
														propagateNowState({
															propagation,
															isSaveQueueRunning: isRunning,
															outstandingCommentaryGenerations: pendingCommentaryGenerations.size,
															canPropagate,
														}) === "disabled"
													}
													data-qualifier="InstrumentCustomization/Sync/PropagateNow"
													onClickAsync={onPropagateNow}
												>
													Apply now
												</Button>
											)}
										</WithAtom>
									)}
								</Row>
							)}
						</Row>
						<Row alignItems="center" gap={8} flexGrow={0} flexShrink={0}>
							<Pagination skip={skip.value} take={take.value} total={rowsQuery.data?.total ?? 0} onChange={skip.set} />
						</Row>
					</Row>
					<div className="flex-1 flex flex-col min-h-0 relative z-0">
						<LocalOverlay
							show={rowsQuery.isFetching}
							className="z-10"
							style={{
								background: "transparent",
							}}
						>
							<CircularProgressBar value="indeterminate" />
						</LocalOverlay>
						<InstrumentSpreadsheet
							rows={rows}
							innerRef={setGridEl}
							columns={initialColumns}
							columnsMetadata={classificationMetadata}
							enqueueSave={enqueueSave}
							onSortChanged={(newOrderBy) => orderBy.set(newOrderBy ? [newOrderBy] : undefined)}
							onChangeCell={(tickerName, patchableField) => {
								runWithErrorReporting(
									async () => {
										const state = await enqueueSave(() => onChangeCell(tickerName, patchableField));
										// Source 2: the PATCH tells us when the propagation is now scheduled.
										if (state) {
											setPropagation((current) => applyPropagationState(current, state));
										}
									},
									{
										area: "global",
										attemptedOperation: {
											message: `edit instrument cell ${tickerName}`,
											payload: JSON.stringify({ tickerName, patchableField }),
										},
									},
								).catch(noop);
							}}
							onCommentaryGenerationStarted={trackCommentaryGeneration}
							{...forward}
						/>
					</div>
				</Card>
			</div>
			<BulkActions
				variant="floating"
				count={selectedNodes.length}
				onClear={() => gridEl?.api.deselectAll()}
				actions={[
					{
						label: "Generate description",
						icon: "sphere-ai",
						onClick: () => {
							const selection = rows.filter((x) => selectedNodes.some((s) => s.tickerName === x.tickerName));
							const tickersWithValidIsin = selection.filter(
								({ type }) => type === "MULTI_ASSET" || type === "SINGLE_STOCK",
							);

							if (tickersWithValidIsin.length === 0) {
								return platformToast({
									children: "Unable to generate description, please select instruments with valid identifier",
									icon: "Portfolio",
									severity: "info",
								});
							}

							spawnYesNoDialog({
								size: "medium",
								header: "Generate description",
								yesButton: "Proceed",
								noButton: "Cancel",
								children: (
									<>
										<Text as="p" type="Body/M/Book" className="mb-2">
											You are about to generate{" "}
											<Text as="span" type="Body/M/Bold">
												{tickersWithValidIsin.length} instrument descriptions
											</Text>
										</Text>
										<Text as="p" type="Body/M/Book">
											Please confirm to proceed.
										</Text>
									</>
								),
								async onSubmitAsync() {
									await runWithErrorReporting(
										async () => {
											await parallelize(
												tickersWithValidIsin.map(({ tickerName }) => async () => {
													await instrumentsRepository.generateInstrumentSphereCommentary(tickerName!);
													// This path does not go through the save queue and its write lands asynchronously,
													// long after the call above resolves, so the flush button has to know about it.
													trackCommentaryGeneration(tickerName!);
												}),
												// NOTE: the option object used to sit inside `.map(…)`, where it was read as the
												// callback's `thisArg` and the concurrency was silently unbounded.
												{ concurrency: 32 },
											);
											platformToast({
												children: "Sphere has taken over your request",
												severity: "info",
												icon: "sphere-ai",
											});
										},
										{
											area: "instrument-customisation",
											attemptedOperation: {
												message: "try to generate instruments description",
											},
										},
									);
								},
							})
								.then(noop)
								.catch(noop);
						},
					},
				]}
			/>
		</div>
	);
}

export default InstrumentCustomizationTable;

function InstrumentSpreadsheet({
	rows,
	columns,
	columnsMetadata,
	onChangeCell,
	onCommentaryGenerationStarted,
	onSortChanged,
	innerRef,
	enqueueSave,
}: {
	rows: UserInstrumentDto[];
	onChangeCell: (tickerName: string, patchableField: PatchableFields) => void;
	/** Called once a commentary generation has been accepted, so the page can track it. */
	onCommentaryGenerationStarted: (tickerName: string) => void;
	columnsMetadata: UserInstrumentClassificationDto[];
	onSortChanged(newOrderBy: OrderBy | undefined): void;
	innerRef: Ref<AgGridReact<UserInstrumentDto>>;
	columns: ColDef<UserInstrumentDto>[];
	/** The page's save-queue wrapper, so a generation dispatched from here also lights the indicator. */
	enqueueSave: TaskQueue["enqueue"];
}) {
	const { t } = useTranslation();
	const onCommentaryGenerationStartedRef = useUnsafeUpdatedRef(onCommentaryGenerationStarted);

	const contextMenuItems = useCallback<GetContextMenuItems<UserInstrumentDto, any>>(
		(params) => [
			"copy",
			"paste",
			{
				name: "Generate description",
				disabled: !(params.node?.data?.type === "MULTI_ASSET" || params.node?.data?.type === "SINGLE_STOCK"),
				action(actionParams) {
					if (!actionParams.node?.data?.tickerName) {
						throw new Error("Instrument missing tickerName field");
					}
					actionParams.node.setDataValue("description", {
						...coalesce(actionParams.node.data.description, {}),
						status: "CALCULATING",
					} satisfies Description);

					const tickerName = actionParams.node.data.tickerName;

					runWithErrorReporting(
						async () => {
							await enqueueSave(() => instrumentsRepository.generateInstrumentSphereCommentary(tickerName));
							// The write lands asynchronously, well after the queued call resolves, so the save
							// queue going idle is no guarantee that the generation is done.
							onCommentaryGenerationStartedRef.current(tickerName);
							platformToast({
								children: "Sphere has taken over your request",
								severity: "info",
								icon: "sphere-ai",
							});
						},
						{
							area: "global",
							attemptedOperation: {
								message: `generate commentary description ${tickerName}`,
								payload: JSON.stringify({ tickerName }),
							},
						},
					).catch(noop);
				},
				icon: renderToStaticMarkup(<Icon icon="sphere-ai" size={16} />),
			},
		],
		[enqueueSave, onCommentaryGenerationStartedRef],
	);

	const onCellValueChanged = useCallback(
		(event: CellValueChangedEvent<UserInstrumentDto, any, any>): void => {
			const hasValueChanged = !equal(event.newValue, event.oldValue);

			if (!hasValueChanged) {
				return;
			}

			const valueToPatch = agCellValueToApi(event, columnsMetadata);
			if (!valueToPatch) {
				platformToast({
					children: t("SOMETHING_WENT_WRONG"),
					severity: "error",
					icon: "Icon-full-alert",
				});
				return;
			}

			onChangeCell(event.data.tickerName!, valueToPatch);
		},
		[columnsMetadata, onChangeCell, t],
	);

	const _onSortChanged = useCallback(
		(params: SortChangedEvent<UserInstrumentDto, any>) => {
			const sortedColumn = params.columns?.find((x) => x.getSort() != null);
			const newOrderBy = sortedColumn
				? ({
						columnName: sortedColumn.getColId(),
						direction: sortedColumn.getSort()!,
					} satisfies OrderBy)
				: undefined;
			onSortChanged(newOrderBy);
		},
		[onSortChanged],
	);

	return (
		<AgGridWithModules<UserInstrumentDto>
			{...agExtra.checkboxColumn({
				mode: "multiRow",
				selectAll: "currentPage",
			})}
			{...agExtra.usePersistColState(persistedColStateKeys["tableV3-instrument-customisation"])}
			columnDefs={columns}
			innerRef={innerRef}
			rowData={rows}
			getRowId={(row) => row.data.tickerName!}
			getContextMenuItems={contextMenuItems}
			onCellValueChanged={onCellValueChanged}
			onSortChanged={_onSortChanged}
			maxConcurrentDatasourceRequests={1}
		/>
	);
}

/** @internal — exported for tests */
export function agCellValueToApi(
	event: CellValueChangedEvent<UserInstrumentDto>,
	instrumentColumns: UserInstrumentClassificationDto[],
): PatchableFields | undefined {
	if (event.colDef.colId === "NAME") {
		return { name: event.newValue };
	}

	if (event.colDef.colId === "ALIAS") {
		return { alias: event.newValue };
	}

	if (event.colDef.colId === "DESCRIPTION") {
		return { description: { value: event.newValue } };
	}

	if (event.colDef.colId === "RISK_MODEL") {
		return { riskModel: event.newValue };
	}

	const column = instrumentColumns.find((c) => c.classificationUuid === event.colDef.colId);

	if (!column) {
		return;
	}

	if (column.fieldType === "QUALITY") {
		return {
			qualities: [
				{
					classificationUuid: column.classificationUuid,
					standardClassificationId: column.standardClassificationId,
					value: event.newValue,
				},
			],
		};
	}

	if (column.fieldType === "SCORE") {
		return {
			scores: [
				{
					classificationUuid: column.classificationUuid!,
					standardClassificationId: column.standardClassificationId,
					value: event.newValue,
				},
			],
		};
	}

	if (column.fieldType === "TAG") {
		return {
			tags: [
				{
					classificationUuid: column.classificationUuid,
					standardClassificationId: column.standardClassificationId,
					values: event.newValue,
				},
			],
		};
	}
}
